import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, JsonPipe } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { ProtestantCanonBooks } from '../../../../models/books.bible.model';
import { Group } from '../../../../models/group.model';
import { Member } from '../../../../models/member.model';
import { MembershipStatus } from '../../../../models/membership.status';
import {
  QuestionType,
  QuizQuestion,
} from '../../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../../models/quiz.model';
import { UserModel } from '../../../../models/user.model';
import { QuizService } from '../../../../services/quiz.service';
import { UtilsService } from '../../../../services/utils.service';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import {
  ChipColor,
  ChipComponent,
} from '../../../components/inputs/chip/chip.component';
import { TooltipComponent } from '../../../components/tooltips/tooltip/tooltip.component';
import { QuestionSearchComponent } from '../../question-search/question-search.component';

@Component({
  selector: 'app-schedule-quiz',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnFabComponent,
    ReactiveFormsModule,
    AsyncPipe,
    JsonPipe,
    ChipComponent,
    BtnActionComponent,
    TooltipComponent,
    QuestionSearchComponent,
  ],
  templateUrl: './schedule-quiz.component.html',
  animations: [
    // Animation for the Backdrop (Fade)
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    // Animation for the Panel (Slide)
    trigger('slideInOut', [
      transition(
        ':enter',
        [
          // Start state: if mobile, below screen; if desktop, off-right
          style({ transform: '{{startPos}}' }),
          animate(
            '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ transform: 'translate(0,0)' }),
          ),
        ],
        { params: { startPos: 'translateX(100%)' } },
      ), // Default param
      transition(
        ':leave',
        [animate('250ms ease-in', style({ transform: '{{startPos}}' }))],
        { params: { startPos: 'translateX(100%)' } },
      ),
    ]),
  ],
  styleUrl: './schedule-quiz.component.css',
})
export class ScheduleQuizComponent implements OnInit, OnDestroy {
  showSearch = false;
  isMobile = false;

  get animationParams() {
    return {
      value: 'visible',
      params: {
        startPos: this.isMobile ? 'translateY(100%)' : 'translateX(100%)',
      },
    };
  }

  store: Store<AppState> = inject(Store<AppState>);
  router: Router = inject(Router);
  fb: FormBuilder = inject(FormBuilder);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;
  quizId: string | null = null;

  user: UserModel | null = null;
  group$: Observable<Group | undefined | null> = new Observable();
  quiz: Quiz = {} as Quiz;
  students$: Observable<Member[]> = of([]);
  session$: Observable<any> = of(null);

  selectedQuestions = signal<QuizQuestion[]>([]);
  addQuestions: string[] = [];
  searchedQuestions$: Observable<QuizQuestion[]> = of([]);

  isLoading = signal(false);
  isWorking = signal(false);
  quizForm: FormGroup;

  chipColor = ChipColor;
  protestantBooks = ProtestantCanonBooks;
  questionTypes = Object.values(QuestionType);
  membershipStatus = MembershipStatus;

  totalScore = computed(() => {
    return this.selectedQuestions().reduce((acc, q) => acc + (q.score || 0), 0);
  });

  get isTimeLimitEnabled(): boolean {
    const val = this.quizForm.get('enableTimeLimit')?.value;
    return val === true || val === 'true';
  }

  myRoles$: Observable<any> = new Observable();
  exit$ = new Subject<void>();

  constructor() {
    this.quizForm = this.fb.group({
      title: [null, [Validators.required]],
      startDate: [dayjs().format('YYYY-MM-DD'), [Validators.required]],
      endDate: [
        dayjs().add(3, 'day').format('YYYY-MM-DD'),
        [Validators.required],
      ],
      totalDuration: [30, [Validators.required, Validators.min(0)]],
      byInvite: [false, [Validators.required]],
      groupId: [this.groupId, [Validators.required]],
      createdById: [null, [Validators.required]],
      createdByName: [null, [Validators.required]],
      enableScoring: [true, [Validators.required]],
      allowMultipleAttempts: [false, [Validators.required]],
      shortAnswerPercentage: [
        50,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      enableTimeLimit: [true, [Validators.required]],
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
  }

  onSearchResults(questions: QuizQuestion[]) {
    this.searchedQuestions$ = of(questions);
  }

  onSearchCleared() {
    this.searchedQuestions$ = of([]);
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit() {
    this.checkScreenSize();
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        this.quizForm.patchValue({
          title: `${this.user?.name}'s Quiz - ${new Date().toLocaleDateString()}`,
          createdById: this.user?.uid,
          createdByName: this.user?.name,
        });
      });

    this.group$ = this.store.select(groupSelector.getGroupById(this.groupId!));

    this.quizForm
      .get('enableTimeLimit')
      ?.valueChanges.pipe(takeUntil(this.exit$))
      .subscribe((enable) => {
        const totalDurationControl = this.quizForm.get('totalDuration');
        if (enable === true || enable === 'true') {
          totalDurationControl?.enable();
          totalDurationControl?.setValidators([
            Validators.required,
            Validators.min(0),
          ]);
        } else {
          totalDurationControl?.disable();
          totalDurationControl?.clearValidators();
        }
        totalDurationControl?.updateValueAndValidity();
      });
  }

  @HostListener('window:resize')
  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  toggleQuestion(q: any, addOrRemoveFlag: boolean) {
    if (addOrRemoveFlag) {
      this.addQuestion(q);
    } else {
      this.removeQuestion(q);
    }
  }

  addQuestion(q: any) {
    if (!this.selectedQuestions().find((item) => item.id === q.id)) {
      this.selectedQuestions.update((questions) => [...questions, q]);
    }
  }

  removeQuestion(q: any) {
    this.selectedQuestions.update((questions) =>
      questions.filter((item) => item.id !== q.id),
    );
  }

  isQuestionSelected(questionId: string): boolean {
    return this.selectedQuestions().some((q) => q.id === questionId);
  }

  createQuiz(draft: boolean = false) {
    if (this.quizForm.invalid) {
      this.logger.error(
        'Missing required information to create quiz session. Group ID: ',
        this.groupId,
        ' User ID: ',
        this.user?.uid,
        ' User Name: ',
        this.user?.name,
      );
      this.utilsService.onShowAlert(
        {
          code: 'ERR_USER_INFO_MISSING',
          title: 'Error Creating Quiz',
          message: 'Missing required information. Please try again later.',
        } as AppExceptionModel,
        false,
      );
      return;
    }

    if (this.selectedQuestions().length === 0) {
      this.logger.error('No questions selected for the quiz.');
      this.utilsService.onShowAlert(
        {
          code: 'NO_QUESTIONS_SELECTED',
          title: 'Error Creating Quiz',
          message: 'Please select at least one question to create a quiz.',
        } as AppExceptionModel,
        false,
      );
      return;
    }

    this.isWorking.set(true);
    this.quiz.byInvite = this.quizForm.value.byInvite;
    this.quiz.inviteCode = UtilsService.createInviteCode();
    this.quiz.questions = this.selectedQuestions().map((q) => q.id!);
    this.quiz.title = this.quizForm.value.title;
    this.quiz.groupId = this.quizForm.value.groupId;
    this.quiz.quizStatus = draft ? QuizStatus.draft : QuizStatus.scheduled;
    this.quiz.totalScore = this.totalScore();
    this.quiz.enableTimeLimit =
      this.quizForm.getRawValue().enableTimeLimit === true ||
      this.quizForm.getRawValue().enableTimeLimit === 'true';
    this.quiz.totalDuration = this.quiz.enableTimeLimit
      ? Number(this.quizForm.getRawValue().totalDuration)
      : 0;
    this.quiz.scheduledAt = this.quizForm.value.startDate
      ? Timestamp.fromDate(new Date(this.quizForm.value.startDate))
      : Timestamp.now();
    this.quiz.endDate = this.quizForm.value.endDate
      ? Timestamp.fromDate(new Date(this.quizForm.value.endDate))
      : Timestamp.fromDate(dayjs().add(3, 'day').toDate());
    this.quiz.isLive = false;
    this.quiz.createdById = this.quizForm.value.createdById;
    this.quiz.createdByName = this.quizForm.value.createdByName;
    this.quiz.enableScoring =
      this.quizForm.value.enableScoring === true ||
      this.quizForm.value.enableScoring === 'true';
    this.quiz.allowMultipleAttempts =
      this.quizForm.value.allowMultipleAttempts === true ||
      this.quizForm.value.allowMultipleAttempts === 'true';
    this.quiz.shortAnswerPercentage = Number(
      this.quizForm.value.shortAnswerPercentage,
    );
    this.quizService
      .createQuiz(this.quiz)
      .then((id) => {
        this.logger.debug('Quiz created with ID: ', id);
        this.router.navigate(['..', id], {
          relativeTo: this.activatedRoute,
          replaceUrl: true,
        });
      })
      .catch((error) => {
        this.logger.error('Error creating quiz session: ', error);
        this.utilsService.onShowAlert(
          {
            code: 'CREATE_QUIZ_ERROR',
            title: 'Error Creating Quiz',
            message: 'Failed to create quiz. Please try again later.',
          } as AppExceptionModel,
          false,
        );
      })
      .finally(() => {
        this.isWorking.set(false);
      });
  }
}
